import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import CreateTripModal from "../../../../components/agent/trips/CreateTripModal";
import EnhancedTripRow from "../../../../components/agent/trips/EnhancedTripRow";
import DeleteConfirmationModal from "../../../../components/agent/DeleteConfirmationModal";
import TripDocumentUploadModal from "../../../../components/agent/trips/TripDocumentUploadModal";
import { usePageAccess } from "../../../../hooks/useAccessControl";
import { 
  Search, 
  Plus,
  Calendar,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Filter,
  DollarSign,
  Target,
  Castle,
  MapPin,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Activity,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import { useUser } from "../../../../context/UserContext";
import usePixieDustNavigation from "../../../../hooks/usePixieDustNavigation";
import "../../../../styles/modern-design-system.css";
import { decodeHtmlEntities } from "../../../../utils/textUtils";
import toast from "../../../../utils/toast";

export default function CRMDisneyland() {
  const [trips, setTrips] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Analytics
  const [analytics, setAnalytics] = useState({
    totalTrips: 0,
    statusBreakdown: {},
    completionRate: 0,
    recentActivity: {
      thisMonth: 0,
      thisWeek: 0
    },
    revenueOverview: {
      totalValue: 0,
      averageValue: 0,
      commissionEarned: 0
    },
    clientEngagement: {
      activeQuestionnaires: 0,
      awaitingResponse: 0
    }
  });

  const { hasAccess, accessInfo } = usePageAccess('DISNEYLAND');
  const location = useLocation();
  const navigate = useNavigate();
  
  // Pixie dust navigation hook
  const {
    navigateToUnifiedItinerary
  } = usePixieDustNavigation();

  // Redirect if no access
  useEffect(() => {
    if (!hasAccess) {
      navigate("/agent/dashboard");
    }
  }, [hasAccess, navigate]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch trips filtered by Disneyland
        const tripsRes = await fetch(`${import.meta.env.VITE_API_URL}/api/trips`, {
          credentials: "include",
        });
        if (!tripsRes.ok) throw new Error("Failed to fetch trips");
        const allTrips = await tripsRes.json();
        
        // Filter for Disneyland trips only
        const disneylandTrips = allTrips.filter(trip => trip.destination === 'disneyland');

        // Initialize trips with basic data first
        setTrips(disneylandTrips);
        calculateAnalytics(disneylandTrips);

        // Fetch commission data using bulk endpoint for better performance
        try {
          const tripIds = disneylandTrips.map(trip => trip.tripid);
          
          if (tripIds.length > 0) {
            const bulkCommissionRes = await fetch(
              `${import.meta.env.VITE_API_URL}/api/bulk/trips/commissions`,
              {
                method: 'POST',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ tripIds })
              }
            );

            if (bulkCommissionRes.ok) {
              const bulkData = await bulkCommissionRes.json();
              const commissionMap = new Map();
              
              // Create a map for quick lookup
              bulkData.data.forEach(commission => {
                commissionMap.set(commission.tripid, commission);
              });

              // Merge commission data with trips
              const tripsWithCommissions = disneylandTrips.map(trip => {
                const commissionData = commissionMap.get(trip.tripid);
                return {
                  ...trip,
                  commission: commissionData?.commission ? {
                    amount: commissionData.commission.amount,
                    rate: commissionData.commission.rate,
                    tripValue: commissionData.trip_value,
                    status: commissionData.commission.status
                  } : commissionData?.fallback_commission ? {
                    amount: commissionData.fallback_commission.amount,
                    rate: commissionData.fallback_commission.rate,
                    tripValue: commissionData.trip_value,
                    status: 'pending'
                  } : null
                };
              });

              setTrips(tripsWithCommissions);
              calculateAnalytics(tripsWithCommissions);
              console.log(`✅ Loaded commission data for ${bulkData.count} Disneyland trips via bulk endpoint`);
            } else {
              console.warn(`Bulk commission API returned ${bulkCommissionRes.status}`);
            }
          }
        } catch (error) {
          console.error('Failed to fetch bulk commission data:', error);
        }

        // Fetch clients for trip-client mapping
        const clientsRes = await fetch(`${import.meta.env.VITE_API_URL}/api/clients`, {
          credentials: "include",
        });
        if (clientsRes.ok) {
          const clientsData = await clientsRes.json();
          setClients(clientsData);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        toast.error("Failed to load trip data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const calculateAnalytics = (tripsData) => {
    const total = tripsData.length;
    const completed = tripsData.filter((t) => t.status === "completed").length;
    const completionRate = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

    // Status breakdown
    const statusBreakdown = tripsData.reduce((acc, trip) => {
      const status = trip.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Recent activity (last 30 days and last 7 days)
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const thisMonth = tripsData.filter(trip => {
      const createdDate = new Date(trip.created_at || trip.createdAt);
      return createdDate >= thirtyDaysAgo;
    }).length;

    const thisWeek = tripsData.filter(trip => {
      const createdDate = new Date(trip.created_at || trip.createdAt);
      return createdDate >= sevenDaysAgo;
    }).length;

    // Revenue overview
    let totalValue = 0;
    let commissionEarned = 0;
    
    tripsData.forEach(trip => {
      if (trip.commission) {
        // Safely parse numeric values
        const tripValue = parseFloat(trip.commission.tripValue) || 0;
        const commissionAmount = parseFloat(trip.commission.amount) || 0;
        
        // Only add if values are valid numbers
        if (!isNaN(tripValue) && tripValue > 0) {
          totalValue += tripValue;
        }
        if (!isNaN(commissionAmount) && commissionAmount > 0) {
          commissionEarned += commissionAmount;
        }
      }
    });

    const averageValue = total > 0 && totalValue > 0 ? Math.round(totalValue / total) : 0;

    // Client engagement
    const activeQuestionnaires = tripsData.filter(trip => 
      trip.status === "pending" || trip.status === "planning"
    ).length;

    const awaitingResponse = tripsData.filter(trip => 
      trip.status === "pending"
    ).length;

    setAnalytics({
      totalTrips: total,
      statusBreakdown,
      completionRate,
      recentActivity: {
        thisMonth,
        thisWeek
      },
      revenueOverview: {
        totalValue,
        averageValue,
        commissionEarned
      },
      clientEngagement: {
        activeQuestionnaires,
        awaitingResponse
      }
    });
  };

  const handleTripCreated = (newTrip) => {
    setTrips([newTrip, ...trips]);
    calculateAnalytics([newTrip, ...trips]);
    toast.success("Trip created successfully!");
  };

  const handleTripStatusUpdate = (tripId, newStatus) => {
    const updatedTrips = trips.map(trip => 
      trip.tripid === tripId ? { ...trip, status: newStatus } : trip
    );
    setTrips(updatedTrips);
    calculateAnalytics(updatedTrips);
  };

  const handleClientStatusUpdate = async (clientId, newStatus) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ relationship_status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update client status');
      }

      // Update local clients state
      const updatedClients = clients.map(client => 
        client.clientid === clientId 
          ? { ...client, relationship_status: newStatus }
          : client
      );
      setClients(updatedClients);
    } catch (error) {
      console.error('Error updating client status:', error);
      toast.error('Failed to update client status');
    }
  };

  const handleCommissionUpdate = async (commissionData) => {
    try {
      // In a real implementation, this would call the backend API
      // For now, we'll just update the local state
      const updatedTrips = trips.map(trip => 
        trip.tripid === commissionData.tripId 
          ? { ...trip, commission: commissionData }
          : trip
      );
      setTrips(updatedTrips);
      calculateAnalytics(updatedTrips);
    } catch (error) {
      console.error('Error updating commission:', error);
      toast.error('Failed to update commission');
    }
  };

  const handleDelete = async (tripId) => {
    try {
      setIsDeleting(true);
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/trips/${tripId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!res.ok) throw new Error("Failed to delete trip");

      const updatedTrips = trips.filter((trip) => trip.tripid !== tripId);
      setTrips(updatedTrips);
      calculateAnalytics(updatedTrips);
      toast.success("Trip deleted successfully!");
      setShowDeleteModal(false);
      setTripToDelete(null);
    } catch (err) {
      console.error("Error deleting trip:", err);
      toast.error("Failed to delete trip");
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (trip) => {
    setTripToDelete(trip);
    setShowDeleteModal(true);
  };

  // Helper function to format trip name with proper destination formatting
  const formatTripName = (tripName) => {
    if (!tripName) return '';
    
    // Split the trip name to separate trip name and destination
    const parts = tripName.split(' - ');
    if (parts.length !== 2) return tripName;
    
    const [tripTitle, destination] = parts;
    
    // Format destination names
    const destinationMap = {
      'disney_world': 'Disney World',
      'disneyland': 'Disneyland',
      'universal_orlando': 'Universal Orlando',
      'disney_cruise': 'Disney Cruise',
      'universal_studios': 'Universal Studios',
      'seaworld': 'SeaWorld',
      'legoland': 'Legoland',
      'busch_gardens': 'Busch Gardens',
      'knotts_berry_farm': 'Knott\'s Berry Farm',
      'six_flags': 'Six Flags'
    };
    
    const formattedDestination = destinationMap[destination.toLowerCase()] || destination;
    return `${tripTitle} - ${formattedDestination}`;
  };

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied to clipboard!");
    } catch {
      toast.error("Failed to copy link. Please try manually.");
    }
  };

  const getClientForTrip = (trip) => {
    return clients.find(client => client.clientid === trip.clientid) || {
      fullname: trip.fullname || "Unknown Client",
      email: trip.clientemail,
      phone: trip.clientphone
    };
  };

  const filteredTrips = trips.filter((trip) => {
    const tripName = trip.tripname?.toLowerCase() || "";
    const client = getClientForTrip(trip);
    const clientName = client.fullname?.toLowerCase() || "";
    
    const matchesSearch = 
      tripName.includes(searchQuery.toLowerCase()) ||
      clientName.includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || trip.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (!hasAccess) {
    return null; // Will redirect
  }

  return (
    <>
      <div className="max-w-[1400px] mx-auto">
            {/* Header */}
            <div className="flex flex-col gap-4 justify-between mb-4 md:flex-row md:items-center animate-fade-in-up">
              <div>
                <h1 className="text-xl font-bold tracking-tight leading-tight text-black sm:text-2xl lg:text-3xl text-balance">
                  Disneyland Trips
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  Advanced Disneyland trip workflow management with status tracking and client integration
                </p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-gray-400 shadow sm:px-5 sm:py-2.5"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Create Disneyland Trip</span>
                <span className="sm:hidden">New Trip</span>
              </button>
            </div>

            {/* Compact Analytics Cards */}
            <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-3 lg:grid-cols-6 animate-fade-in-up">
              
              {/* 1. Total Trips */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <Castle className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Disneyland</span>
                </div>
                <p className="text-xl font-bold text-black">{analytics.totalTrips}</p>
                <p className="text-xs text-gray-500">Total trips</p>
              </div>

              {/* 2. Completion Rate */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <TrendingUp className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Completion</span>
                </div>
                <p className="text-xl font-bold text-black">{analytics.completionRate}%</p>
                <div className="mt-1 w-full h-1 bg-gray-200 rounded-full">
                  <div 
                    className="h-1 bg-blue-600 rounded-full transition-all duration-300" 
                    style={{ width: `${analytics.completionRate}%` }}
                  ></div>
                </div>
              </div>

              {/* 3. Pending Trips */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <Clock className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Pending</span>
                </div>
                <p className="text-xl font-bold text-black">{analytics.statusBreakdown.pending || 0}</p>
                <p className="text-xs text-gray-500">Awaiting action</p>
              </div>

              {/* 4. Recent Activity */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">This Week</span>
                </div>
                <p className="text-xl font-bold text-black">{analytics.recentActivity.thisWeek}</p>
                <p className="text-xs text-gray-500">New trips</p>
              </div>

              {/* 5. Revenue Overview */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <DollarSign className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Total Value</span>
                </div>
                <p className="text-lg font-bold text-black">
                  ${analytics.revenueOverview.totalValue > 0 ? analytics.revenueOverview.totalValue.toLocaleString() : '0'}
                </p>
                <p className="text-xs text-gray-500">Trip values</p>
              </div>

              {/* 6. Client Engagement */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <Users className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Active</span>
                </div>
                <p className="text-xl font-bold text-black">{analytics.clientEngagement.activeQuestionnaires}</p>
                <p className="text-xs text-gray-500">Questionnaires</p>
              </div>

            </div>

            {/* Enhanced Filters and Search */}
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between animate-fade-in-up">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1 sm:flex-none">
                  <label htmlFor="trip-search" className="sr-only">Search trips</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                    <input
                      id="trip-search"
                      type="text"
                      placeholder="Search Disneyland trips or clients..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="py-2 pr-3 pl-10 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="flex flex-col">
                  <button
                    className={`crm-filter-btn ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter size={18} />
                    <span>Filters</span>
                    {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>
              
              <div className="text-xs text-gray-600 sm:text-sm">
                {filteredTrips.length} of {trips.length} Disneyland trips
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="p-4 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">Status</label>
                    <div className="relative">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 pr-8 w-full text-sm text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400"
                        style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Trips List */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="p-3 border-b border-gray-200 sm:p-4">
                <div>
                  <h2 className="text-base font-semibold text-black sm:text-lg">
                    Disneyland Trips
                  </h2>
                  <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                    Manage Disneyland trip workflow and track completion status
                  </p>
                </div>
              </div>

              <div className="p-3 sm:p-4">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="flex gap-2 items-center">
                      <div className="w-6 h-6 rounded-full border-2 border-black animate-spin border-t-transparent"></div>
                      <span className="text-sm text-gray-600">Loading Disneyland trips...</span>
                    </div>
                  </div>
                ) : filteredTrips.length === 0 ? (
                  <div className="flex flex-col justify-center items-center px-6 py-12 w-full text-center">
                    <Castle className="mb-4 w-12 h-12 text-gray-400" />
                    <h2 className="mb-2 text-lg font-semibold text-gray-800 sm:text-xl">
                      No Disneyland Trips Found
                    </h2>
                    <p className="mb-6 max-w-md text-sm text-gray-600">
                      {searchQuery || statusFilter !== "all"
                        ? "Try adjusting your search or filters."
                        : "You haven't created any Disneyland trips yet. Click 'Create Disneyland Trip' to get started."
                      }
                    </p>
                    {!searchQuery && statusFilter === "all" && (
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="inline-block px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md shadow transition hover:bg-blue-700 sm:px-5"
                      >
                        Create Disneyland Trip
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 animate-fade-in-up">
                    {filteredTrips.map((trip) => (
                      <EnhancedTripRow
                        key={trip.tripid || trip.id}
                        trip={trip}
                        client={getClientForTrip(trip)}
                        onClick={() => navigateToUnifiedItinerary(trip.tripid)}
                        onUnifiedNavigation={navigateToUnifiedItinerary}
                        onDelete={() => openDeleteModal(trip)}
                        onCopyLink={handleCopyLink}
                        onStatusUpdate={handleTripStatusUpdate}
                        onClientStatusUpdate={handleClientStatusUpdate}
                        onCommissionUpdate={handleCommissionUpdate}
                        hasCRMAccess={hasAccess}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

      {/* Create Trip Modal */}
      {showCreateModal && (
        <CreateTripModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onTripCreated={handleTripCreated}
          defaultDestination="disneyland"
        />
      )}
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTripToDelete(null);
        }}
        onConfirm={() => handleDelete(tripToDelete?.tripid)}
        title="Delete Disneyland Trip"
        message="Are you sure you want to delete this Disneyland trip? This action cannot be undone."
        itemName={tripToDelete ? `${decodeHtmlEntities(tripToDelete.tripname)} - ${tripToDelete.destination}` : ''}
        formatItemName={formatTripName}
        itemType="trip"
        isLoading={isDeleting}
        confirmText="Delete Trip"
        cancelText="Cancel"
      />
    </>
  );
}
